import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { IconButton } from '../primitives/index.js';
import { Icon } from '../icons/index.js';

export type ToastTone = 'success' | 'danger' | 'warning' | 'neutral';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
}

interface ToastContextValue {
  push: (toast: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Success auto-dismisses; an error stays until acknowledged. Max three. */
const AUTO_DISMISS_MS = 5000;
const MAX_STACK = 3;

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { ...toast, id }].slice(-MAX_STACK));
      if (toast.tone !== 'danger') {
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`} role="status">
            <div className="toast__body">
              <p className="toast__title">{toast.title}</p>
              {toast.body === undefined ? null : <p>{toast.body}</p>}
            </div>
            <IconButton label="Dismiss" onClick={() => dismiss(toast.id)}>
              <Icon name="close" size="sm" />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider');
  return context;
}
